import React from 'react'
import { makeStyles } from '@mui/styles'
import ThemeInterface from '@/theme-interface'
import lang from '@/lang'
import { useAppSelector } from '@/redux/store'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import ButtonSquare, { VARIANTS } from '@/modules/base/components/buttons/square/button-square'
import SquareIconButton, { VARIANTS as ICON_VARIANTS, SIZES } from '@/modules/base/components/buttons/square-icon-button/square-icon-button'
import WarningIcon from '@assets/images/icons/exclamation_mark.svg'
import CloseIcon from '@assets/images/icons/close.svg'
import { usePostHogConsentNotice } from '@/modules/posthog/consent-notice'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  root: {
    gridArea: 'notice',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 20,
    minHeight: 36,
    padding: '0 20px',
    background: theme.orangeBackground,
    borderBottom: `1px solid ${theme.palette.warning.main}`,
    color: theme.palette.text.primary,
    fontSize: theme.fontSizes.small,
    fontWeight: theme.fontWeights.semiBold,

    [theme.devices.mobile]: {
      display: 'none',
    },
  },
  message: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  // subtle text links instead of filled buttons; both choices look the same on purpose
  linkButton: {
    '&&': {
      height: 'auto',
      width: 'auto',
      padding: '4px 8px',
      background: 'transparent',
      border: 'none',
      color: theme.palette.primary.main,
      fontSize: theme.fontSizes.small,
      fontWeight: theme.fontWeights.medium,
      '&:hover': {
        background: 'transparent',
        color: theme.palette.primary.dark,
        textDecoration: 'underline',
      },
    },
  },
}), { name: 'PostHogConsentNotice' })

const PostHogConsentNotice = () => {
  const classes = useStyles()
  const isDemo = useAppSelector(state => state.user.info?.activeTenant?.isDemo)
  const { visible, accept, showDetails, dismiss } = usePostHogConsentNotice()

  // the demo header uses the same slot and takes precedence
  if (!visible || isDemo) {
    return null
  }

  return (
    <div className={classes.root} role="region" aria-label={lang.posthogConsentNotice.text} data-qs-id="posthog-consent-notice">
      <div className={classes.message}>
        <SVGIcon svg={WarningIcon} width="16px" height="16px" />
        <span>{lang.posthogConsentNotice.text}</span>
      </div>
      <div className={classes.actions}>
        <ButtonSquare
          variant={VARIANTS.small}
          customClass={classes.linkButton}
          label={lang.posthogConsentNotice.accept}
          onClick={accept}
          dataQsId="posthog-consent-notice-accept"
        />
        <ButtonSquare
          variant={VARIANTS.small}
          customClass={classes.linkButton}
          label={lang.posthogConsentNotice.details}
          onClick={showDetails}
          dataQsId="posthog-consent-notice-details"
        />
        <SquareIconButton
          icon={CloseIcon}
          variant={ICON_VARIANTS.icon}
          size={SIZES.small}
          ariaLabel={lang.posthogConsentNotice.close}
          tooltip={lang.posthogConsentNotice.close}
          onClick={dismiss}
          dataQsId="posthog-consent-notice-close"
        />
      </div>
    </div>
  )
}

export default PostHogConsentNotice
